import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Play, Radio, Trash2, Trophy, ExternalLink, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useGames, useDeleteGame, useMe } from "@/lib/queries";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import type { Game } from "@/lib/types";

// Date-grouped list of every game in the active org. Delete is the
// principal action; tap any row to open its detail page.
export default function GamesList() {
  const { data: games = [], isLoading } = useGames();
  const { data: me } = useMe();
  const del = useDeleteGame();

  // Owner/admin can delete any; member can only delete games they created.
  const myRole =
    me?.orgs.find(
      (o) =>
        o.orgSlug ===
        (typeof window !== "undefined"
          ? localStorage.getItem("activeOrgSlug")
          : null),
    )?.role ?? me?.orgs[0]?.role ?? null;
  const isOrgAdmin =
    me?.isPlatformAdmin || myRole === "owner" || myRole === "admin";

  // Group by playedOn (descending). games already arrives sorted desc by
  // playedOn + createdAt from the API.
  const groups = useMemo(() => {
    const m = new Map<string, Game[]>();
    for (const g of games) {
      const list = m.get(g.playedOn) ?? [];
      list.push(g);
      m.set(g.playedOn, list);
    }
    return Array.from(m.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [games]);

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
            Games
          </h1>
          <p className="text-muted-foreground text-sm">
            Every game logged in this org. Newest first.
          </p>
        </div>
        <Button asChild size="sm">
          <Link href="/dashboard/games/new">
            <Play size={14} /> Log game
          </Link>
        </Button>
      </header>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading games…</p>
      ) : games.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <Trophy
              className="mx-auto text-muted-foreground"
              size={32}
            />
            <p className="text-sm text-muted-foreground">
              No games logged yet. Tap "Log game" to record your first.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map(([date, list]) => (
            <section key={date} className="space-y-2">
              <h2 className="text-xs font-display uppercase tracking-widest text-muted-foreground">
                {formatDate(date)}
              </h2>
              <ul className="divide-y divide-border rounded-lg border border-border bg-card overflow-hidden">
                {list.map((g) => (
                  <GameRow
                    key={g.id}
                    game={g}
                    canDelete={
                      isOrgAdmin || g.createdByUserId === me?.id
                    }
                    onDelete={async () => {
                      try {
                        await del.mutateAsync(g.id);
                      } catch (e) {
                        alert((e as ApiError).message ?? "Could not delete");
                      }
                    }}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(yyyyMmDd: string): string {
  // Avoid Date timezone surprises by parsing the parts directly.
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  if (!y || !m || !d) return yyyyMmDd;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function GameRow({
  game,
  canDelete,
  onDelete,
}: {
  game: Game;
  canDelete: boolean;
  onDelete: () => void | Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="group">
      <div className="flex items-center gap-3 p-3">
        <Link
          href={`/dashboard/games/${game.id}`}
          className="flex-1 min-w-0 flex items-center gap-3"
        >
          <div className="shrink-0 w-14 space-y-1">
            <StatusPill status={game.status} />
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-[9px] font-display uppercase tracking-wider",
                game.isVirtual ? "text-primary" : "text-muted-foreground",
              )}
            >
              {game.isVirtual ? <Boxes size={9} /> : <Play size={9} />}
              {game.isVirtual ? "Virtual" : "Live"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div
              className={cn(
                "text-sm truncate",
                game.winningTeam === 1
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {game.team1Players.map((p) => p.name).join(" & ") || "—"}
            </div>
            <div
              className={cn(
                "text-sm truncate",
                game.winningTeam === 2
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {game.team2Players.map((p) => p.name).join(" & ") || "—"}
            </div>
          </div>
          <div className="shrink-0 grid grid-rows-2 text-right font-display tabular-nums text-sm leading-tight px-2 py-1 rounded border border-border bg-background min-w-[3rem]">
            <span
              className={cn(
                game.winningTeam === 1 ? "text-primary font-semibold" : "text-muted-foreground",
              )}
            >
              {game.team1Score}
            </span>
            <span
              className={cn(
                game.winningTeam === 2 ? "text-primary font-semibold" : "text-muted-foreground",
              )}
            >
              {game.team2Score}
            </span>
          </div>
          <ExternalLink
            size={12}
            className="text-muted-foreground opacity-30 hidden sm:block shrink-0"
          />
        </Link>

        {canDelete && !confirming && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-destructive/10 opacity-50 group-hover:opacity-100 shrink-0"
            onClick={() => setConfirming(true)}
            aria-label="Delete game"
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>

      {confirming && (
        <div className="px-3 pb-3 pt-1 flex items-center justify-end gap-2 bg-destructive/5 border-t border-destructive/20">
          <span className="text-xs text-muted-foreground mr-auto">
            Delete this game permanently?
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirming(false)}
          >
            Keep
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              await onDelete();
              setConfirming(false);
            }}
          >
            Delete
          </Button>
        </div>
      )}
    </li>
  );
}

function StatusPill({ status }: { status: Game["status"] }) {
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-display uppercase tracking-widest text-primary">
        <Radio size={10} className="animate-pulse" />
        live
      </span>
    );
  }
  if (status === "voided") {
    return (
      <span className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">
        voided
      </span>
    );
  }
  if (status === "scheduled") {
    return (
      <span className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">
        scheduled
      </span>
    );
  }
  return null; // complete — no pill (the score itself tells the story)
}
